'use client';

import React from 'react';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { Col, Row } from 'reactstrap';
import type { ActivityLogEntry } from 'src/types';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import { updateVisibility } from 'src/redux/modals/slice';
import { MediaItem } from 'src/types/quote-approval';


type AvailabilitySlot = NonNullable<ActivityLogEntry['event_attributes']['availability']>[number];

const ALL_TIME_SLOTS = ['morning', 'afternoon', 'evening'];

function groupAvailabilityByDate(slots: AvailabilitySlot[]): Record<string, AvailabilitySlot[]> {
	return slots.reduce<Record<string, AvailabilitySlot[]>>((acc, slot) => {
		if (!acc[slot.date]) {
			acc[slot.date] = [];
		}
		acc[slot.date].push(slot);
		return acc;
	}, {});
}

function formatTimeLabel(slot: AvailabilitySlot): string {
	const label = slot.time_slot
		? slot.time_slot.charAt(0).toUpperCase() + slot.time_slot.slice(1)
		: '';
	const range = slot.time_range ? ` (${slot.time_range})` : '';
	return `${label}${range}`;
}

function formatAvailabilityLine(date: string, slots: AvailabilitySlot[]): string {
	const dateLabel = moment(date, 'YYYY-MM-DD').format('ddd MMMM D');
	const isAnytime = ALL_TIME_SLOTS.every((timeSlotName) => slots?.some((slot) => slot?.time_slot === timeSlotName));
	const timeLabel = isAnytime ? 'Anytime' : slots?.map(formatTimeLabel).join(', ');
	return `${dateLabel} ${timeLabel}`;
}

interface LumiIntakeEventCardProps {
	logDetails: ActivityLogEntry;
}

const LumiIntakeEventCard: React.FC<LumiIntakeEventCardProps> = ({
	logDetails,
}) => {
	const dispatch = useAppDispatch();
	const timeZone = useAppSelector(
		(state) => state?.jobDetails?.data?.timezone,
	) as string | undefined;

	const attrs = logDetails?.event_attributes;

	const {
		summaries,
		media,
		availability,
		property_access,
	} = attrs;

	const handleImageClick = (collection: MediaItem[], index: number) => {
		if (isEmpty(collection)) return;
		dispatch(
			updateVisibility({
				modalType: 'imageSlidesModal',
				isVisible: true,
				extraProps: {
					modalData: {
						collection,
						position: index,
						showDeleteButton: false,
						imageType: 'lumi-intake-photos',
						timeZone,
					},
				},
			}),
		);
	};

	const renderPhotos = (photoList: MediaItem[]) => {
		if (isEmpty(photoList)) return null;
		return (
			<div className="preview-image-inner mt-2">
				{photoList.slice(0, 4).map((photo, i) => (
					<div
						key={photo.job_media_id}
						className={`preview-image ${photo.media_type === 'video' ? 'video' : ''}`}
					>
						<img
							src={photo.thumbnail_url || photo.url}
							alt=""
							onClick={() => handleImageClick(photoList, i)}
						/>
					</div>
				))}
				{photoList.length > 4 && (
					<div
						className="preview-image"
						onClick={() => handleImageClick(photoList, 0)}
					>
						+{photoList.length - 4}
					</div>
				)}
			</div>
		);
	};

	return (
		<>
			{summaries?.map((summary, i) => {
				const issueMedia = (media ?? []).filter((m) => summary?.media_ids?.includes(m?.job_media_id));
				return (
					<div key={summary.resident_report} className="lumi-intake-card__issue">
						<div className="lumi-intake-card__issue-title">Issue #{i + 1}</div>
						<div className="body-xs-bold mb-1">What the resident reported</div>
						<div className="body-xs text-default-lighter">{summary.resident_report}</div>
						{summary.troubleshooting_summary && (
							<>
								<div className="body-xs-bold mb-1 mt-2">Troubleshooting Summary</div>
								<div className="body-xs text-default-lighter lumi-intake-card__troubleshooting-summary">{summary.troubleshooting_summary}</div>
							</>
						)}
						{renderPhotos(issueMedia)}
					</div>
				);
			})}
			<hr />
			<div className="body-xs-bold mb-1">Resident availability</div>
			{availability && availability.length > 0 ? (
				<ul>
					{Object.entries(groupAvailabilityByDate(availability))?.map(([date, slots]) => (
						<li className="text-default-lighter" key={date}>
							{formatAvailabilityLine(date, slots)}
						</li>
					))}
				</ul>
			) : (
				<div className="body-xs text-default-lighter">N/A</div>
			)}
			<hr />
			<div className="body-xs-bold mb-1">Property info</div>
			{property_access ? (
				<Row>
					<Col>
						<div className="body-xs text-default-lighter">Access Info:</div>
						<div className="body-xs text-default-lighter">
							{property_access?.entry_details || 'N/A'}
						</div>
					</Col>
					<Col>
						<div className="body-xs text-default-lighter">Pets:</div>
						<div className="body-xs text-default-lighter">
							{property_access?.pets && property_access.pets.length > 0
								? property_access.pets.join(', ')
								: 'N/A'}
						</div>
					</Col>
				</Row>
			) : (
				<div className="body-xs text-default-lighter">N/A</div>
			)}
			<hr />
			<div className="body-xs-bold mb-1">Additional notes</div>
			<div className="body-xs text-default-lighter">
				{property_access?.additional_notes || 'N/A'}
			</div>
		</>
	);
};

export default LumiIntakeEventCard;
